import React, { useState } from "react";
import { motion } from "framer-motion";

const headlineContainerVariants = {
  hidden: {},
  visible: (delayChildren = 0) => ({
    transition: {
      staggerChildren: 0.08,
      delayChildren: delayChildren,
    },
  }),
};

const letterVariants = {
  hidden: { opacity: 0, y: 50 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 1.1,
      ease: [0.16, 1, 0.3, 1],
    },
  },
};

export default function FooterNatural({ className }) {
  const [email, setEmail] = useState("");
  const [subscribed, setSubscribed] = useState(false);

  const handleSubscribe = (e) => {
    e.preventDefault();
    if (email.trim()) {
      setSubscribed(true);
      setEmail("");
      setTimeout(() => setSubscribed(false), 5000);
    }
  };

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link href="https://fonts.googleapis.com/css2?family=Big+Shoulders+Display:wght@700;900&family=Inter:wght@400;500;600;700&display=swap" rel="stylesheet" />

      <footer className={"w-full bg-[#F2EFE9] text-[#242121] px-6 py-10 sm:px-10 sm:py-14 md:px-14 md:py-16 lg:px-20 lg:py-20 overflow-hidden antialiased " + (className || "")}>
        <div className="w-full max-w-[1580px] mx-auto flex flex-col justify-between gap-12 lg:gap-16">

          <div className="w-full flex flex-col md:flex-row justify-between items-start md:items-top gap-6 md:gap-8 pb-4">
            <div className="flex-1">
              <motion.h2
                className="tracking-tighter"
                style={{
                  fontFamily: '"Big Shoulders Display", sans-serif',
                  fontSize: "clamp(68px, 17vw, 250px)",
                  fontWeight: 900,
                  lineHeight: "0.82",
                  letterSpacing: "-0.035em",
                  color: "#4D5B44",
                  margin: 0,
                  textTransform: "uppercase",
                }}
                variants={headlineContainerVariants}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {"NATURAL".split("").map((char, index) => (
                  <motion.span
                    key={"footer-char-" + index}
                    variants={letterVariants}
                    className="inline-block"
                  >
                    {char}
                  </motion.span>
                ))}
              </motion.h2>
            </div>

            <div className="pt-2 md:pt-4 md:text-right shrink-0">
              <motion.p
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 1.1, delay: 0.4, ease: [0.16, 1, 0.3, 1] }}
                style={{
                  fontFamily: '"Big Shoulders Display", sans-serif',
                  fontSize: "clamp(28px, 4.2vw, 60px)",
                  fontWeight: 700,
                  color: "#4D5B44",
                  margin: 0,
                  lineHeight: "1.05",
                  letterSpacing: "-0.02em",
                  textTransform: "uppercase",
                }}
              >
                Created for life
              </motion.p>
            </div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5, ease: [0.16, 1, 0.3, 1] }}
            className="w-full grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-start pt-2"
          >

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-8 sm:gap-6 lg:gap-8">

              <div className="flex flex-col gap-3">
                <span
                  className="text-xs sm:text-sm font-bold tracking-[1.5px] uppercase text-[#4D5B44]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  CONTACT
                </span>
                <div
                  className="flex flex-col gap-1 text-[15px] sm:text-[16px] font-normal text-[#242121]/90 leading-snug"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <p>Solune Cosmetics AS</p>
                  <a href="mailto:hello@soluneglow.world" className="hover:underline transition-all">
                    hello@soluneglow.world
                  </a>
                  <p>+1 800 555 0199</p>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <span
                  className="text-xs sm:text-sm font-bold tracking-[1.5px] uppercase text-[#4D5B44]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  FOLLOW US
                </span>
                <div
                  className="flex flex-col gap-1.5 text-[15px] sm:text-[16px] font-normal text-[#242121]/90"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <a href="https://instagram.com" target="_blank" rel="noreferrer" className="hover:underline transition-all">
                    Instagram
                  </a>
                  <a href="https://linkedin.com" target="_blank" rel="noreferrer" className="hover:underline transition-all">
                    LinkedIn
                  </a>
                  <a href="https://tiktok.com" target="_blank" rel="noreferrer" className="hover:underline transition-all">
                    TikTok
                  </a>
                </div>
              </div>

              <div className="flex flex-col gap-3">
                <span
                  className="text-xs sm:text-sm font-bold tracking-[1.5px] uppercase text-[#4D5B44]"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  LANGUAGE
                </span>
                <div
                  className="flex flex-col gap-1.5 text-[15px] sm:text-[16px] font-normal text-[#242121]/90"
                  style={{ fontFamily: "Inter, sans-serif" }}
                >
                  <span className="font-semibold cursor-pointer">English</span>
                  <span className="opacity-75 hover:opacity-100 transition-opacity cursor-pointer">Norsk</span>
                  <span className="opacity-75 hover:opacity-100 transition-opacity cursor-pointer">Français</span>
                </div>
              </div>

            </div>

            <div className="lg:col-span-5 flex flex-col gap-3 lg:pl-6">
              <span
                className="text-xs sm:text-sm font-bold tracking-[1.5px] uppercase text-[#4D5B44]"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                NEWSLETTER
              </span>
              <p
                className="text-sm sm:text-[15px] font-normal text-[#242121]/90"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                Sign up for updates & botanical releases
              </p>

              <form onSubmit={handleSubscribe} className="w-full mt-2 relative">
                <div className="flex items-center justify-between border-b-2 border-[#4D5B44] pb-2 w-full group">
                  <input
                    type="email"
                    required
                    placeholder="EMAIL"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent border-none outline-none text-[#4D5B44] placeholder-[#4D5B44]/60 uppercase leading-none pr-4"
                    style={{
                      fontFamily: '"Big Shoulders Display", sans-serif',
                      fontSize: "clamp(28px, 3.8vw, 44px)",
                      fontWeight: 700,
                    }}
                  />
                  <button
                    type="submit"
                    className="text-[#4D5B44] text-2xl sm:text-3xl lg:text-4xl hover:translate-x-2 transition-transform cursor-pointer shrink-0 pl-2"
                    aria-label="Subscribe"
                  >
                    →
                  </button>
                </div>

                {subscribed && (
                  <motion.p
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="text-[#4D5B44] text-xs font-semibold mt-2"
                    style={{ fontFamily: "Inter, sans-serif" }}
                  >
                    ✓ Thank you! You have been subscribed to our updates.
                  </motion.p>
                )}
              </form>

              <p
                className="text-xs sm:text-[14px] text-[#242121]/75 mt-3 leading-relaxed"
                style={{ fontFamily: "Inter, sans-serif" }}
              >
                By subscribing, you agree to receive news and product updates from Solune Cosmetics AS.
              </p>
            </div>

          </motion.div>

          <div className="w-full pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 text-xs sm:text-[14px] text-[#242121]/80 font-medium border-t border-[#4D5B44]/20" style={{ fontFamily: "Inter, sans-serif" }}>
            <div>
              <span>Made by Solune Glow</span>
            </div>

            <div className="flex items-center gap-6">
              <a href="#privacy" className="hover:underline transition-all">Privacy Policy</a>
              <a href="#terms" className="hover:underline transition-all">Terms of Service</a>
            </div>

            <div>
              <span>Copyright © {new Date().getFullYear()} Solune Cosmetics AS.</span>
            </div>
          </div>

        </div>
      </footer>
    </>
  );
}